import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";
import api from "@/lib/api";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
      setUnread((u) => Math.max(0, u - 1));
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (n.path) navigate(n.path);
  };

  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger data-testid="nav-notifications" className="relative p-2 rounded-full hover:bg-black/5 transition-colors outline-none">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF5A00] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[340px] p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/5">
          <p className="font-semibold text-sm">Notifications</p>
          {unread > 0 && (
            <button data-testid="notif-mark-all" onClick={markAll} className="flex items-center gap-1 text-xs font-medium text-[#FF5A00] hover:underline">
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="notif-empty">
              <Bell className="w-6 h-6 mx-auto mb-2 opacity-30" />
              You're all caught up.
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                data-testid={`notif-item-${n.id}`}
                onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 border-b border-black/5 hover:bg-black/[0.03] transition-colors flex gap-3 ${n.read ? "" : "bg-[#FFF6F0]"}`}
              >
                <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-[#FF5A00]"}`} />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold truncate">{n.title}</span>
                  <span className="block text-xs text-muted-foreground leading-snug mt-0.5">{n.message}</span>
                  <span className="block text-[11px] text-muted-foreground/70 mt-1">{timeAgo(n.created_at)}</span>
                </span>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
